import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ReportAnalytics = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [searchQuery, setSearchQuery] = useState('');

  // Sample data
  const stats = [
    { id: 1, title: 'Total Revenue', value: '₦25,400', trend: 'up', completed: false },
    { id: 2, title: 'Total Ticket Sold', value: '1,843', trend: 'up', completed: true },
    { id: 3, title: 'Total Refund Issues', value: '12', trend: 'down', completed: true },
    { id: 4, title: 'Net Revenue', value: '₦24,800', trend: 'up', completed: true }
  ];

  const salesReport = [
    { id: 1, no: 1, eventName: 'Lagos Music Fest', ticketType: 'VIP', ticketSold: 150, totalRevenue: '₦10,000', discountUsed: '50 Used' },
    { id: 2, no: 2, eventName: 'Lagos Music Fest', ticketType: 'General', ticketSold: 20, totalRevenue: '₦1,000', discountUsed: '50 Used' }
  ];

  const refundData = [
    { id: 1, no: 1, refundId: '#234567', attendeeName: 'Lagos Music Fest', eventName: 'Music Fest', ticketType: 'VIP', refundAmount: '$100', status: 'Pending' },
    { id: 2, no: 2, refundId: '#234567', attendeeName: 'Lagos Music Fest', eventName: 'Music Fest', ticketType: 'General', refundAmount: '₦100', status: 'Approved' }
  ];

  const engagementData = [
    { id: 1, no: 1, attendeeName: 'Joy Omotosho', referrals: 20, shares: 20, reviews: 10, coinsEarned: '10 coins' },
    { id: 2, no: 2, attendeeName: 'Dele Janet', referrals: 20, shares: 20, reviews: 15, coinsEarned: '15 coins' }
  ];

  const revenueBreakdown = [
    { id: 1, no: 1, eventName: 'Lagos Music Fest', eventDate: '15 apr 2025', ticketType: 'VIP', ticketSold: 150, ticketPrice: '₦1,000', totalRevenue: '₦10,000', refundsIssued: '₦1,000', netRevenue: '₦10,000' },
    { id: 2, no: 2, eventName: 'Lagos Music Fest', eventDate: '16 apr 2025', ticketType: 'General', ticketSold: 20, ticketPrice: '₦500', totalRevenue: '₦1,000', refundsIssued: '₦120,000', netRevenue: '₦10,000' },
    { id: 3, no: 3, eventName: 'Tech Conference', eventDate: '22 apr 2025', ticketType: 'Early bird', ticketSold: 200, ticketPrice: '₦10,000', totalRevenue: '₦10,000', refundsIssued: '₦10,000', netRevenue: '₦10,000' }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto bg-[#F5F5F5] h-screen">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        <h1 className="text-[24px] font-semibold text-[#06044B]">Report & Analytics</h1>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
      >
        {stats.map((stat) => (
          <motion.div
            key={stat.id}
            whileHover={{ y: -5 }}
            className={`bg-white rounded-xl shadow-sm p-6 border-l-4 ${stat.completed ? 'border-green-500' : 'border-indigo-500'}`}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">{stat.title}</h3>
              <div className={`p-1 rounded-full ${stat.trend === 'up' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d={stat.trend === 'up' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} 
                  />
                </svg>
              </div>
            </div>
            <motion.p 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="text-2xl font-bold mt-2 text-gray-900"
            >
              {stat.value}
            </motion.p>
          </motion.div>
        ))}
      </motion.div>

      {/* Tabs */}
      <div className="flex justify-between space-x-0 mb-6 bg-white rounded-lg p-1 shadow-sm border border-gray-200 w-fit">
        {[
          { id: 'sales', label: 'Sales Report' },
          { id: 'refund', label: 'Refund & Cancellation' },
          { id: 'engagement', label: 'Attendee Engagement & Reward' },
          { id: 'revenue', label: 'Event Insights & Trends' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-[15px] font-normal relative rounded-md transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#06044B] text-white' 
                : 'text-[#888888] hover:bg-gray-100 border border-[#E3E3E3]'
            }`}
          >
            {activeTab === tab.id && (
              <motion.div 
                layoutId="activeTabIndicator"
                className="absolute inset-0 rounded-md   z-0"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
            <span className="relative z-10">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-6"
      >
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports..."
            className="block w-full h-[44px] pl-10 pr-3 py-2 border border-gray-300 rounded-[50px] leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: activeTab === 'sales' ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: activeTab === 'sales' ? -20 : 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Sales Report Tab */}
          {activeTab === 'sales' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Sales Report (Revenue & Ticket Performance)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Sold</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount Used</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {salesReport.map((item) => (
                      <motion.tr
                        key={item.id}
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                        className="transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.no}.</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.eventName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketSold}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.totalRevenue}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.discountUsed}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Refund & Cancellation Tab */}
          {activeTab === 'refund' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Refund & Cancellation</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendee Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {refundData.map((item) => (
                      <motion.tr
                        key={item.id}
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                        className="transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.no}.</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.refundId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.attendeeName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.eventName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.refundAmount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            item.status === 'Approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Attendee Engagement Tab */}
          {activeTab === 'engagement' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Attendee Engagement & Rewards (Referral & Loyalty)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendee Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrals</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviews</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coins Earned</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {engagementData.map((item) => (
                      <motion.tr
                        key={item.id}
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                        className="transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.no}.</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.attendeeName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.referrals}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.shares}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.reviews}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-yellow-600">{item.coinsEarned}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Revenue Breakdown Tab */}
          {activeTab === 'revenue' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Revenue Breakdown by Event</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Sold</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refunds Issued</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {revenueBreakdown.map((item) => (
                      <motion.tr
                        key={item.id}
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                        className="transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.no}.</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.eventName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.eventDate}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketSold}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ticketPrice}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.totalRevenue}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">{item.refundsIssued}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{item.netRevenue}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ReportAnalytics;